import { ReactNode } from "react";
import { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface EditorialCardProps {
  number?: string;
  icon?: LucideIcon;
  title: string;
  children: ReactNode;
  variant?: "default" | "ink" | "danger";
  /** Optional accent color for the icon + number stamp */
  accent?: "red" | "ochre" | "blue" | "green" | "stamp";
  className?: string;
}

const accentMap = {
  red: { text: "text-destructive", bg: "bg-destructive/10" },
  ochre: { text: "text-warning", bg: "bg-warning/15" },
  blue: { text: "text-info", bg: "bg-info/10" },
  green: { text: "text-success", bg: "bg-success/10" },
  stamp: { text: "text-primary", bg: "bg-primary/10" },
};

/**
 * Luminous Atrium card:
 * - Rounded-3xl soft surface with subtle shadow
 * - Pill number stamp, Instrument Sans medium title
 * - Lifts on hover with sun-glow shadow
 */
export const EditorialCard = ({
  number,
  icon: Icon,
  title,
  children,
  variant = "default",
  accent,
  className,
}: EditorialCardProps) => {
  const tone = {
    default:
      "bg-card/90 backdrop-blur-sm border border-border/50 text-foreground hover:border-primary/30 hover:shadow-[0_24px_60px_-24px_rgba(62,92,77,0.18)]",
    ink:
      "bg-foreground border border-foreground text-background hover:shadow-[0_24px_60px_-20px_rgba(21,34,27,0.4)]",
    danger:
      "bg-card/90 backdrop-blur-sm border border-border/50 text-foreground hover:border-destructive/30 hover:shadow-[0_24px_60px_-24px_rgba(220,75,75,0.16)]",
  }[variant];

  const accentTone = accent ? accentMap[accent] : null;

  return (
    <article
      className={cn(
        "rounded-3xl p-7 sm:p-8 md:p-10 transition-all duration-300 hover:-translate-y-1",
        tone,
        className,
      )}
    >
      <div className="flex items-center justify-between mb-7 md:mb-9">
        {number && (
          <span
            className={cn(
              "inline-flex items-center justify-center min-w-[2.25rem] h-9 rounded-full px-3 text-[11px] font-medium tracking-[-0.01em] tabular-nums",
              accentTone
                ? cn(accentTone.text, accentTone.bg)
                : variant === "ink"
                ? "text-background/70 bg-background/10"
                : "text-muted-foreground bg-secondary",
            )}
          >
            {number}
          </span>
        )}
        {Icon && (
          <span
            className={cn(
              "inline-flex h-10 w-10 items-center justify-center rounded-full",
              accentTone
                ? cn(accentTone.bg, accentTone.text)
                : variant === "ink"
                ? "bg-background/10 text-background"
                : "bg-secondary text-foreground",
            )}
          >
            <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
          </span>
        )}
      </div>
      <h3
        className={cn(
          "font-sans text-xl sm:text-2xl font-medium leading-tight tracking-[-0.01em] mb-3 md:mb-4",
          variant === "ink" ? "text-background" : "text-foreground",
        )}
      >
        {title}
      </h3>
      <div
        className={cn(
          "font-sans text-[15px] md:text-base leading-relaxed",
          variant === "ink" ? "text-background/75" : "text-muted-foreground",
        )}
      >
        {children}
      </div>
    </article>
  );
};
